import { create } from 'zustand';
import type { LimitErrorMeta, PaywallPayload } from '@/shared/lib/api';
import type { LimitPaywallCode } from '@/shared/stores/limit-paywall-store';

/**
 * Which Pass the paywall opens highlighted. Pro Plan is the only sellable pass:
 * Designer was fully removed (it collapsed into the Pro Pass), and the Tokens
 * Pro Pass itself went off sale in 2026-08 — so an `open_pass_paywall` action
 * still carrying `pro_pass` maps to nothing and is a no-op, instead of opening
 * a sheet with nothing to buy.
 */
export type PassPaywallHighlight = 'pro_plan';

/** The token-paywall dialog state to restore when the Pass sheet is backed
 *  out of via `returnTo` — mirrors `LimitPaywallState`'s open payload. */
export interface PassPaywallReturnTo {
  payload: PaywallPayload;
  code: LimitPaywallCode;
  meta: LimitErrorMeta | null;
}

/**
 * App-wide presenter for the Pass paywall dialog.
 *
 * Mirrors `useLimitPaywallStore` + Apple's `PassPaywallPresenter`: the dialog
 * is mounted once at the root and any caller just runs `open(highlight)`.
 *
 * Pro Plan is the only product it sells (`GET /passes` serves the sellable
 * catalog). Locked render controls no longer route here — they open the Facade
 * Kit through the limit paywall.
 */
interface PassPaywallState {
  highlight: PassPaywallHighlight | null;
  /**
   * Set when this sheet was opened FROM the token-paywall dialog (the
   * "Pro Plan" alternative-action card). The token dialog closes itself
   * before opening this one (two stacked modal Dialogs double the overlay
   * dim — see LimitPaywallDialog), so the back button here restores it via
   * `useLimitPaywallStore.showPaywall(...)` instead of just closing.
   */
  returnTo: PassPaywallReturnTo | null;
  open: (highlight: PassPaywallHighlight, returnTo?: PassPaywallReturnTo) => void;
  close: () => void;
}

export const usePassPaywallStore = create<PassPaywallState>((set) => ({
  highlight: null,
  returnTo: null,
  open: (highlight, returnTo) => set({ highlight, returnTo: returnTo ?? null }),
  close: () => set({ highlight: null, returnTo: null }),
}));
